import type { AxiosRequestConfig } from 'axios'
import merge from 'lodash.merge'

import type { ExtendedChangeLog, MigrationResult, SiteId } from '@beaded/models'

import type { SiteWithExtras } from 'types/Site'
import type { Obj, ID } from 'types/common'

import * as apiUtils from '.'

const url = '/site'

export const get = (
  id: ID,
  options: AxiosRequestConfig = {},
): Promise<{ site: SiteWithExtras }> =>
  apiUtils.GET(`${url}/${id}`, merge({}, apiUtils.extrasFlags, options))

export const getAll = (
  options: AxiosRequestConfig = {},
): Promise<{ sites: SiteWithExtras[] }> =>
  apiUtils.GET(`${url}/`, merge({}, apiUtils.extrasFlags, options))

export const create = (data: Obj) => apiUtils.POST(`${url}/`, data)

export const bulkCreate = (projectId: ID, file: File) => {
  const formData = new FormData()
  formData.append('file', file, file.name)
  return apiUtils.POST(`${url}/${projectId}/bulk`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

export const bulkEdit = (file: File) => apiUtils.PUT(`${url}/bulk`, file)

export const update = (id: ID, data: Obj) => apiUtils.PUT(`${url}/${id}`, data)

export const remove = (id: ID) => apiUtils.DELETE(`${url}/${id}`)

/**
 * Rename channels of a vendor site (project admins and above). A label of null
 * resets that channel to its automatic name.
 */
export const setSensorLabels = (
  id: ID,
  labels: Array<{ sensorSns: string[]; label: string | null }>,
): Promise<{ updated: number }> =>
  apiUtils.PUT(`${url}/${id}/sensor-labels`, { labels })

/**
 * Customer edit of the labels of an EXISTING site cable-history row. Labels
 * only; the server answers 404 for a row that is not the site's.
 */
export const updateCableHistoryLabels = (
  siteId: ID,
  historyId: string,
  labels: unknown,
) =>
  apiUtils.PUT(`${url}/${siteId}/cable-history/${historyId}/labels`, {
    labels,
  })

export const getChangeLog = (
  id: SiteId,
  options: AxiosRequestConfig = {},
): Promise<{ changelog: ExtendedChangeLog[] }> =>
  apiUtils.GET(`${url}/${id}/changelog`, options)

export const getMenuProjection = (options: AxiosRequestConfig = {}) =>
  apiUtils.GET(`${url}/`, merge({ params: { projection: 'menu' } }, options))

export const migrateToV3 = (id: ID): Promise<MigrationResult> =>
  apiUtils.POST(`/admin/migrate-site-to-v3/${id}`, {})

export const fixHistoryDate = (
  id: string,
  type: 'logger' | 'cable',
  startedAt: string,
): Promise<{ success: boolean }> =>
  apiUtils.PUT(`/admin/fix-history-date/${id}`, { type, startedAt })

export const publicPage = {
  get: (
    id: ID,
    options: AxiosRequestConfig = {},
  ): Promise<{ site: SiteWithExtras }> =>
    apiUtils.GET(
      `/public${url}/${id}`,
      merge({}, apiUtils.extrasFlags, options),
    ),
  getAll: (
    options: AxiosRequestConfig = {},
  ): Promise<{ sites: SiteWithExtras[] }> =>
    apiUtils.GET(`/public${url}/`, merge({}, apiUtils.extrasFlags, options)),
}
